/**
 * Left panel: list of steps, add/delete, DnD, selection.
 */

import { useState } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { Button } from "../../primitives/Button";
import type { ProcessStepTemplateStudioOut } from "../../../api/opsV2/types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  studioAddStep,
  studioDeleteStep,
  studioReorderStep,
} from "../../../api/opsV2/client";
import { getCurrentOrgId } from "../../../state/orgContext";

type StepsPanelProps = {
  steps: ProcessStepTemplateStudioOut[];
  selectedStepId: string | null;
  onSelectStep: (id: string | null) => void;
  onStepsChange: (steps: ProcessStepTemplateStudioOut[]) => void;
  templateId: string;
};

function StepRow({
  step,
  isSelected,
  onSelect,
  onDelete,
}: {
  step: ProcessStepTemplateStudioOut;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 rounded border cursor-pointer group ${
        isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
      } ${isDragging ? "opacity-50" : ""}`}
      onClick={onSelect}
    >
      <button type="button" className="touch-none cursor-grab active:cursor-grabbing p-0.5 text-muted" {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
        <GripVertical size={14} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{step.title}</div>
        <div className="text-xs text-muted">SLA: {step.sla_days ?? "—"} j</div>
      </div>
      <button
        type="button"
        className="opacity-0 group-hover:opacity-100 p-1 text-muted hover:text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Supprimer"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export function StepsPanel({ steps, selectedStepId, onSelectStep, onStepsChange, templateId }: StepsPanelProps) {
  const [adding, setAdding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = steps.findIndex((s) => s.id === active.id);
    const newIndex = steps.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(steps, oldIndex, newIndex);
    onStepsChange(reordered);
    try {
      const orgId = getCurrentOrgId();
      await studioReorderStep(templateId, active.id as string, newIndex + 1, orgId);
    } catch (e) {
      onStepsChange(steps);
      alert(e instanceof Error ? e.message : "Erreur réordonnancement");
    }
  };

  const handleAddStep = async () => {
    setAdding(true);
    try {
      const orgId = getCurrentOrgId();
      const newStep = await studioAddStep(
        templateId,
        {
          order_index: steps.length,
          title: `Étape ${steps.length + 1}`,
          description: null,
          sla_days: null,
          completion_policy: "all_required",
        },
        orgId
      );
      onStepsChange([...steps, newStep as unknown as ProcessStepTemplateStudioOut]);
      onSelectStep(newStep.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erreur ajout étape");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!confirm("Supprimer cette étape ?")) return;
    try {
      const orgId = getCurrentOrgId();
      await studioDeleteStep(templateId, stepId, orgId);
      onStepsChange(steps.filter((s) => s.id !== stepId));
      if (selectedStepId === stepId) onSelectStep(steps[0]?.id ?? null);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erreur suppression");
    }
  };

  return (
    <div className="p-2 flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-text">Étapes</h3>
        <Button label="Ajouter" intent="ghost" size="sm" onClick={handleAddStep} state={adding ? "loading" : "default"} />
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1 overflow-auto flex-1">
            {steps.length === 0 && <p className="text-xs text-muted py-2">Aucune étape. Cliquez sur Ajouter.</p>}
            {steps.map((step) => (
              <StepRow
                key={step.id}
                step={step}
                isSelected={selectedStepId === step.id}
                onSelect={() => onSelectStep(step.id)}
                onDelete={() => handleDeleteStep(step.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
